"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, Cpu, ArrowRight, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const operationalBullets = [{
  icon: ShieldCheck,
  title: "Regulated Compliance Assurance",
  description: "Method validation, IQ/OQ qualification, and audit-ready data workflows for GMP environments."
}, {
  icon: Cpu,
  title: "Application-Led Process Integration",
  description: "Tailored NIR, Raman, and powder testing configurations aligned to your specific manufacturing line."
}, {
  icon: CheckCircle2,
  title: "Rapid Technical Feasibility",
  description: "Direct access to senior spectroscopy specialists and application engineers across Ireland and the UK."
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-lg border border-border">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Request Received</h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid work email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Project scope summary or message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message *</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." className="min-h-28" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full font-medium" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing Request...
          </> : <>
            Submit Consultation Request
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationrequest" data-nav="light" className="bg-background py-16 sm:py-24 border-t border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: 5 Cols */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <Badge variant="secondary" className="mb-4">
                Application Support
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
                Discuss Your Analytical Application
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed mb-8">
                Connect with our technical application specialists to evaluate spectrometer fit, feasibility studies, qualification requirements, and full lifecycle support for your laboratory or manufacturing environment.
              </p>
            </div>

            <div className="space-y-6 pt-2">
              {operationalBullets.map((bullet, i) => {
              const IconComponent = bullet.icon;
              return <div key={bullet.title} data-index={i} className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary border border-border">
                      <IconComponent className="size-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground font-default">
                        {bullet.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-normal">
                        {bullet.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column: 7 Cols */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-7">
            <Card className="border border-border shadow-md">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription>
                  Complete the consultation details below and an application specialist will contact you within one business day.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="contact_form" sectionName="consultation-request" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Submit Consultation Request" successMessage="Thank you for reaching out. Our analytical engineering team has received your consultation request and will follow up promptly.">
                  <ConsultationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}